import { useEffect, useState, useRef, useCallback } from "react";
import io from "socket.io-client";
import Login from "./Login";

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const SERVER_URL = isDevelopment ? "http://localhost:4000" : "https://server.whambambagels.com";
const socket = io(SERVER_URL);

const CACHE_KEY = "whambam_items";
const QUEUE_KEY = "whambam_queue";

function loadCachedItems() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || []; } catch { return []; }
}
function saveCachedItems(items) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(items)); } catch {}
}
function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY)) || []; } catch { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch {}
}

// Resize/compress an image file (handles large mobile photos) and return a JPEG Blob
function compressImage(file, maxDim = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load image"));
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > width && height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error("Could not compress image")),
          "image/jpeg",
          quality
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [items, setItems] = useState(loadCachedItems);
  const [activeLocation, setActiveLocation] = useState("Overall Inventory");
  const [hideZero, setHideZero] = useState(false);
  const [showPrepOnly, setShowPrepOnly] = useState(false);
  const [showPics, setShowPics] = useState(false);
  const [showLittlePics, setShowLittlePics] = useState(false);
  const [showStElmoPics, setShowStElmoPics] = useState(false);
  const [showCubesmartPics, setShowCubesmartPics] = useState(false);
  const [showRevcon1b, setShowRevcon1b] = useState(false);
  const [revconExpanded, setRevconExpanded] = useState(null);
  const [showOrderStatus, setShowOrderStatus] = useState(false);
  const [showPrepShopList, setShowPrepShopList] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showWeeklyStats, setShowWeeklyStats] = useState(false);
  const [weeklyOrders, setWeeklyOrders] = useState([]);
  const [showKDS, setShowKDS] = useState(false);
  const [showGrill, setShowGrill] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [photos, setPhotos] = useState({});
  const [orders, setOrders] = useState([]);
  const [isOnline, setIsOnline] = useState(socket.connected);
  const [addingTo, setAddingTo] = useState(null);
  const [newItemName, setNewItemName] = useState("");
  const [newItemIsPercent, setNewItemIsPercent] = useState(false);
  const [newItemPrepList, setNewItemPrepList] = useState(false);
  const [newItemShopList, setNewItemShopList] = useState(false);
  const [newItemThreshold, setNewItemThreshold] = useState(2);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editPrepList, setEditPrepList] = useState(false);
  const [editShopList, setEditShopList] = useState(false);
  const [editThreshold, setEditThreshold] = useState(2);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isPhone, setIsPhone] = useState(window.innerWidth <= 480);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const longPressTimer = useRef(null);
  const queueRef = useRef(loadQueue());

  const flushQueue = useCallback(() => {
    const q = queueRef.current;
    if (q.length === 0) return;
    q.forEach(action => socket.emit("adjust", action));
    queueRef.current = [];
    saveQueue([]);
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth in development mode
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        setUser({ isAuthorized: true, email: 'dev@localhost' });
        setAuthLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${SERVER_URL}/auth/current-user`, {
          credentials: 'include'
        });
        const data = await response.json();
        setUser(data.user);
      } catch (err) {
        console.error('Auth check failed:', err);
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const handleItems = (serverItems) => {
      setItems(serverItems);
      saveCachedItems(serverItems);
    };

    const handleConnect = () => {
      setIsOnline(true);
      flushQueue();
      socket.emit("getItems");
    };

    const handleDisconnect = () => {
      setIsOnline(false);
    };

    socket.on("items", handleItems);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (socket.connected) {
      flushQueue();
      socket.emit("getItems");
    }
    fetchPhotos();
    fetchOrders();

    return () => {
      socket.off("items", handleItems);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [flushQueue]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsPhone(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-refresh orders every 5 seconds when Order Status page is active
  useEffect(() => {
    if (!showOrderStatus) return;
    
    fetchOrders(); // Initial fetch
    const interval = setInterval(() => {
      fetchOrders();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [showOrderStatus]);

  // Auto-refresh orders every 5 seconds when KDS page is active
  useEffect(() => {
    if (!showKDS) return;
    
    fetchOrders(); // Initial fetch
    const interval = setInterval(() => {
      fetchOrders();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [showKDS]);

  // Auto-refresh orders every 5 seconds when Statistics page is active
  useEffect(() => {
    if (!showStatistics) return;

    fetchOrders(selectedDate);
    const interval = setInterval(() => {
      fetchOrders(selectedDate);
    }, 5000);

    return () => clearInterval(interval);
  }, [showStatistics, selectedDate]);

  // Auto-refresh orders every 5 seconds when Weekly Stats page is active
  useEffect(() => {
    if (!showWeeklyStats) return;

    fetchWeeklyOrders(selectedDate);
    const interval = setInterval(() => {
      fetchWeeklyOrders(selectedDate);
    }, 5000);

    return () => clearInterval(interval);
  }, [showWeeklyStats, selectedDate]);

  const fetchPhotos = () => {
    fetch(`${SERVER_URL}/photos`, {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(setPhotos)
      .catch(() => {});
  };

  const uploadPhoto = async (category) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = async () => {
      const file = input.files[0];
      document.body.removeChild(input);
      if (!file) return;
      try {
        let upload = file;
        try {
          upload = await compressImage(file);
        } catch (e) {
          console.warn("Image compression failed, uploading original:", e);
        }
        const form = new FormData();
        form.append("photo", upload, "photo.jpg");
        const res = await fetch(`${SERVER_URL}/upload/${encodeURIComponent(category)}`, {
          method: "POST",
          body: form,
          credentials: 'include'
        });
        if (!res.ok) {
          const msg = await res.text().catch(() => "");
          throw new Error(`Upload failed (${res.status}) ${msg}`.trim());
        }
        fetchPhotos();
      } catch (err) {
        console.error("Photo upload failed:", err);
        alert("Photo upload failed: " + err.message);
      }
    };
    input.click();
  };

  const fetchOrders = async (date = null) => {
    try {
      const url = date ? `${SERVER_URL}/orders?date=${date}` : `${SERVER_URL}/orders`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      const orders = await res.json();
      setOrders(orders);
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  const fetchWeeklyOrders = async (date = null) => {
    try {
      const url = date ? `${SERVER_URL}/orders/weekly?date=${date}` : `${SERVER_URL}/orders/weekly`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      const orders = await res.json();
      setWeeklyOrders(orders);
    } catch (err) {
      console.error("Error fetching weekly orders:", err);
    }
  };

  const calculateWaitTime = () => {
    // Return default if orders is not an array
    if (!Array.isArray(orders)) return 5;
    
    // Get all orders being prepared (not ready)
    const cookingOrders = orders.filter(order => 
      !order.itemsWithModifiers?.some(item => item.fulfillmentStatus === "READY")
    );
    
    // Count Breakfast and Bagel items
    let breakfastBagelCount = 0;
    cookingOrders.forEach(order => {
      order.itemsWithModifiers?.forEach(item => {
        const itemName = item.name.toLowerCase();
        if (itemName.includes('breakfast') || itemName.includes('bagel')) {
          breakfastBagelCount++;
        }
      });
    });
    
    // Calculate wait time: (count / 4) * 5, minimum 5 minutes
    const waitTime = Math.max(5, Math.ceil((breakfastBagelCount / 4) * 5));
    return waitTime;
  };

  const picCategories = ["Ice Bin", "Meat Fridge", "Drip Fridge Left", "Drip Fridge Right", "Milk Fridge", "Cold Station", "Cold Fridge", "Flattop Fridge Top", "Flattop Fridge Bottom", "Syrups", "Espresso Cups Top", "Espresso Cups Bottom", "Hotel Pans", "Sink", "Opening Bagel Count", "Closing Bagel Count", "Inventory Sheet"];

  const littlePicCategories = ["Meat Fridge LP", "Milk Fridge Left LP", "Milk Fridge Right LP", "Flattop Fridge Top LP", "Flattop Fridge Bottom LP", "Espresso Cups LP", "Syrups LP", "Beans Hopper LP", "Beans Storage LP", "LP Inventory Sheet"];

  const stElmoPicCategories = ["Shelf TL", "Shelf TR", "Shelf ML", "Shelf MR", "Shelf BL", "Shelf BR", "Top of Lowboy", "Inside Lowboy", "Freezer Top","Freezer Bottom", "Left of Freezer", "Inside Big Fridge", "Propane Empties", "Propane Full"];

  const cubesmartPicCategories = ["Shelf 1", "Shelf 2", "Shelf 3", "Shelf 4", "Slee Fridge Top", "Slee Fridge Bottom", "Slee Freezer", "Slee Deep Freeze 1", "Slee Deep Freeze 2"];

  const renderPicsGrid = (categories) => {
    if (isMobile) {
      return (
        <div style={styles.picsGrid}>
          {categories.map(cat => (
            <div key={cat} style={styles.picCard}>
              <h3 style={styles.picTitle}>
                {cat}
                {photos[cat] && photos[cat].updatedAt && (
                  <span style={styles.picTimestamp}>
                    {new Date(photos[cat].updatedAt).toLocaleString()}
                  </span>
                )}
              </h3>
              <div style={styles.picArea} onClick={() => uploadPhoto(cat)}>
                {photos[cat] ? (
                  <img src={photos[cat].src} alt={cat} style={styles.picImage} />
                ) : (
                  <div style={styles.picPlaceholder}>Tap to upload</div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    const count = categories.length;
    const cols = Math.min(count, Math.ceil(Math.sqrt(count) * 1.5));
    const rows = Math.ceil(count / cols);
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: 10,
        height: "calc(100vh - 52px)",
        padding: 10,
        boxSizing: "border-box"
      }}>
        {categories.map(cat => (
          <div key={cat} style={styles.picCardDesktop}>
            <h3 style={styles.picTitleDesktop}>
              {cat}
              {photos[cat] && photos[cat].updatedAt && (
                <span style={styles.picTimestamp}>
                  {new Date(photos[cat].updatedAt).toLocaleString()}
                </span>
              )}
            </h3>
            <div style={styles.picAreaDesktop} onClick={() => uploadPhoto(cat)}>
              {photos[cat] ? (
                <img src={photos[cat].src} alt={cat} style={styles.picImageDesktop} />
              ) : (
                <div style={styles.picPlaceholder}>Tap to upload</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const percentageItems = ["Whole Milk Left", "Lemonade Left", "Orange Juice Left", "Top Eggs Flats Left", "Bottom Eggs Flats Left", "Mixed Pork Left", "Mixed Turkey Left", "Grilled Onions Left", "Grilled Peppers Left", "Mixed Greens", "BJ CC Left", "Herb CC Left", "Plain CC Left", "Scallion CC Left", "Tomatos Left", "Shallots Left", "Capers Left", "Strawberry CC Left", "Walnut Honey Left", "Cut Jalepenos Left", "Walnuts", "Cucumbers", "Butter", "Half and Half Left", "Mustard Left", "Mayo (Small) Left", "Cheddar Left", "American Left", "Gruyere Left", "Pepperjack Left"];

  const baseFridgeCategories = {
    "Meat Fridge": ["Bacon in Plastic", "Bacon in 3-Pan", "American Cheese", "Cheddar", "Grueyere", "Pork Sausage", "Turkey Sausage", "Pepper Jack", "Grilled Red Peppers", "Grilled Onions", "Mixed Pork Sausage", "Mixed Turkey Sausage", "Pastrami", "Smokey Ham", "Walnuts"],
    "Drip Fridge": ["Eggs # Flats", "Mexican Coke", "Mandarin Jarritos", "Whole Milk # Gallons", "Orange Juice # Gallons", "Lemonade # Gallons", "Shallots", "Shiitake Mushrooms", "Tomatoes", "Red Peppers", "Jalapenos", "Avocados"],
    "Milk Fridge": ["Whole Milk Left", "Oat Milk", "Almond Milk", "Lemonade Left", "Orange Juice Left", "Richards Still", "Half and Half Left", "Mustard Left", "Mayo (Small) Left"],
    "Cold Station": ["BJ CC Left", "Herb CC Left", "Plain CC Left", "Scallion CC Left", "Tomatos Left", "Shallots Left", "Capers Left", "Strawberry CC Left", "Walnut Honey Left", "Cut Jalepenos Left"],
    "Cold Fridge": ["Bacon Jalapeno CC", "Herb CC", "Scallion CC", "Strawberry CC", "Walnut Honey CC", "Sundried Tomato CC", "Olive CC", "Mixed Greens", "Lox Fillets", "Cilantro", "Cut Jalepenos", "Chicken Portions", "Butter", "Cucumbers"],
    "Flattop Fridge": ["Top Eggs Flats Left", "Bottom Eggs Flats Left", "Mixed Pork Left", "Mixed Turkey Left", "Grilled Onions Left", "Grilled Peppers Left", "Cheddar Left", "American Left", "Gruyere Left", "Pepperjack Left", "Pastrami Left", "Smokey Ham Left"],
    "La Marzocco": [],
    "Steering Wheel": [],
    "Milk Crate": []
  };

  const fridgeCategories = {};
  Object.keys(baseFridgeCategories).forEach(k => {
    fridgeCategories[k] = [...baseFridgeCategories[k]];
  });
  items.forEach(item => {
    if (item.fridgeCategory) {
      if (!fridgeCategories[item.fridgeCategory]) fridgeCategories[item.fridgeCategory] = [];
      if (!fridgeCategories[item.fridgeCategory].includes(item.name)) {
        fridgeCategories[item.fridgeCategory].push(item.name);
      }
    }
  });

  const isPercentageItem = (item) => {
    return percentageItems.includes(item.name) || item.isPercentage;
  };

  const handleAddItem = (cat) => {
    if (!newItemName.trim()) return;
    socket.emit("addItem", { 
      name: newItemName.trim(), 
      fridgeCategory: cat, 
      isPercentage: newItemIsPercent,
      prepList: newItemPrepList,
      shopList: newItemShopList,
      threshold: newItemThreshold
    });
    setNewItemName("");
    setNewItemIsPercent(false);
    setNewItemPrepList(false);
    setNewItemShopList(false);
    setNewItemThreshold(2);
    setAddingTo(null);
  };

  const handleDeleteItem = (id) => {
    socket.emit("deleteItem", { id });
    setDeleteConfirmId(null);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetItem, category) => {
    e.preventDefault();
    if (!draggedItem || draggedItem._id === targetItem._id) {
      setDraggedItem(null);
      return;
    }

    // Get all items in this category
    const categoryItems = items.filter(item => {
      if (showRevcon1b) {
        const fridgeCat = fridgeCategories[category] || [];
        return fridgeCat.includes(item.name);
      } else {
        return item.category === category;
      }
    }).sort((a, b) => (a.order || 0) - (b.order || 0));

    // Find indices
    const draggedIndex = categoryItems.findIndex(i => i._id === draggedItem._id);
    const targetIndex = categoryItems.findIndex(i => i._id === targetItem._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    // Reorder within category
    const reordered = [...categoryItems];
    const [removed] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, removed);

    // Get IDs of all items (not just this category) in new order
    const otherItems = items.filter(item => {
      if (showRevcon1b) {
        const fridgeCat = fridgeCategories[category] || [];
        return !fridgeCat.includes(item.name);
      } else {
        return item.category !== category;
      }
    });

    const allItemsReordered = [...otherItems, ...reordered].sort((a, b) => (a.order || 0) - (b.order || 0));
    const itemIds = allItemsReordered.map(i => i._id);

    socket.emit("reorderItems", { itemIds });
    setDraggedItem(null);
  };

  const startEditItem = (item) => {
    setEditingItemId(item._id);
    setEditPrepList(item.prepList || false);
    setEditShopList(item.shopList || false);
    setEditThreshold(item.threshold || 2);
  };

  const saveEditItem = () => {
    if (!editingItemId) return;
    socket.emit("updateItemFlags", {
      id: editingItemId,
      prepList: editPrepList,
      shopList: editShopList,
      threshold: editThreshold
    });
    setEditingItemId(null);
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
    setEditPrepList(false);
    setEditShopList(false);
    setEditThreshold(2);
  };

  const startLongPress = (id) => {
    longPressTimer.current = setTimeout(() => {
      setDeleteConfirmId(id);
    }, 800);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

const adjustItem = (id, delta) => {
  if (activeLocation === "Overall Inventory") return;
  const action = { id, location: activeLocation, delta };
  if (socket.connected) {
    socket.emit("adjust", action);
  } else {
    queueRef.current.push(action);
    saveQueue(queueRef.current);
    setItems(prev => {
      const updated = prev.map(item => {
        if (item._id !== id) return item;
        const newItem = { ...item, counts: (item.counts || []).map(c => {
          if (c.location !== activeLocation) return c;
          return { ...c, value: Math.max(0, Math.min(50, (c.value || 0) + delta)) };
        })};
        return newItem;
      });
      saveCachedItems(updated);
      return updated;
    });
  }
};

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <Login />;
  }

  // Show unauthorized message if user is not authorized
  if (!user.isAuthorized) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: 'system-ui', textAlign: 'center', padding: 20 }}>
        <div>
          <h1>Access Pending</h1>
          <p>Your account ({user.email}) is awaiting authorization.</p>
          <p>Please contact an administrator to grant you access.</p>
          <button 
            onClick={() => window.location.href = `${SERVER_URL}/auth/logout`}
            style={{ marginTop: 20, padding: '10px 20px', fontSize: 16, cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {!isOnline && (
        <div style={styles.offlineBanner}>Offline — changes will sync when reconnected</div>
      )}
      {sidebarOpen && (
        <div style={styles.sidebarOverlay} onClick={() => setSidebarOpen(false)} />
      )}
      <div style={{ ...styles.sidebar, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)" }}>
        <div style={styles.sidebarHeader}>
          <span style={styles.sidebarTitle}>WhamBam</span>
          <button style={styles.sidebarClose} onClick={() => setSidebarOpen(false)}>✕</button>
        </div>
        <div style={styles.sidebarNav}>
          <button
            onClick={() => { setActiveLocation("Overall Inventory"); setShowPics(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(!showPics && !showRevcon1b && !showOrderStatus && !showPrepShopList && !showKDS && !showGrill && !showStatistics && !showWeeklyStats && activeLocation === "Overall Inventory" ? styles.sidebarNavItemActive : null) }}
          >
            📦 Overall Inventory
          </button>
          <button
            onClick={() => { setShowPics(true); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showPics ? styles.sidebarNavItemActive : null) }}
          >
            📸 Big Pink Pics
          </button>
          <button
            onClick={() => { setShowLittlePics(true); setShowPics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showLittlePics ? styles.sidebarNavItemActive : null) }}
          >
            📷 Little Pink Pics
          </button>
          <button
            onClick={() => { setShowStElmoPics(true); setShowPics(false); setShowLittlePics(false); setShowCubesmartPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showStElmoPics ? styles.sidebarNavItemActive : null) }}
          >
            🗄️ St. Elmo Storage Pics
          </button>
          <button
            onClick={() => { setShowCubesmartPics(true); setShowStElmoPics(false); setShowPics(false); setShowLittlePics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showCubesmartPics ? styles.sidebarNavItemActive : null) }}
          >
            📦 Other Storage Pics
          </button>
          <button
            onClick={() => { setActiveLocation("St. Elmo Storage Unit"); setShowPics(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(!showPics && !showRevcon1b && !showOrderStatus && !showPrepShopList && !showKDS && !showGrill && !showStatistics && !showWeeklyStats && activeLocation === "St. Elmo Storage Unit" ? styles.sidebarNavItemActive : null) }}
          >
            🏪 St. Elmo Storage
          </button>
          <button
            onClick={() => { setShowPics(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowRevcon1b(true); setActiveLocation("Revcon 1"); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showRevcon1b ? styles.sidebarNavItemActive : null) }}
          >
            🚐 Revcon 1b
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowOrderStatus(false); setShowPrepShopList(true); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showPrepShopList ? styles.sidebarNavItemActive : null) }}
          >
            📋 Prep/Shop List
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowOrderStatus(true); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showOrderStatus ? styles.sidebarNavItemActive : null) }}
          >
            🟡 Order Status
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowGrill(true); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showGrill ? styles.sidebarNavItemActive : null) }}
          >
            🔥 Grill
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowWeeklyStats(false); setShowKDS(true); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showKDS ? styles.sidebarNavItemActive : null) }}
          >
            🍳 KDS
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(true); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowWeeklyStats(false); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showStatistics ? styles.sidebarNavItemActive : null) }}
          >
            📊 Statistics
          </button>
          <button
            onClick={() => { setShowPics(false); setShowRevcon1b(false); setShowOrderStatus(false); setShowPrepShopList(false); setShowStatistics(false); setShowLittlePics(false); setShowStElmoPics(false); setShowCubesmartPics(false); setShowWeeklyStats(true); setShowKDS(false); setShowGrill(false); setSidebarOpen(false); }}
            style={{ ...styles.sidebarNavItem, ...(showWeeklyStats ? styles.sidebarNavItemActive : null) }}
          >
            📈 Weekly Stats
          </button>
          <div style={styles.sidebarDivider} />
          <label style={styles.sidebarCheckbox}>
            <input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} />
            Hide 0 Count
          </label>
          <label style={styles.sidebarCheckbox}>
            <input type="checkbox" checked={showPrepOnly} onChange={e => setShowPrepOnly(e.target.checked)} />
            Finished Prep Stuff
          </label>
        </div>
      </div>
      <div style={styles.topBar}>
        <button style={styles.hamburgerButton} onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <span style={styles.hamburgerLine} />
          <span style={styles.hamburgerLine} />
          <span style={styles.hamburgerLine} />
        </button>
        <span style={styles.topBarTitle}>
          {showOrderStatus ? "Order Status" : showGrill ? "Grill" : showKDS ? "KDS" : showStatistics ? "Statistics" : showWeeklyStats ? "Weekly Stats" : showPrepShopList ? "Prep/Shop List" : showRevcon1b ? "Revcon 1b" : showPics ? "Big Pink Pics" : showLittlePics ? "Little Pink Pics" : showStElmoPics ? "St. Elmo Storage Pics" : showCubesmartPics ? "Cubesmart Storage Pics" : activeLocation === "St. Elmo Storage Unit" ? "St. Elmo Storage" : "Overall Inventory"}
        </span>
        <div style={{ ...styles.onlineDot, background: isOnline ? "#00d97e" : "#f76707" }} title={isOnline ? "Online" : "Offline"} />
      </div>

      {showOrderStatus ? (
        <div style={styles.ordersContainer}>
          <div style={{...styles.ordersGrid, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr"}}>
            <div style={styles.ordersColumn}>
              <div style={styles.columnHeader}>
                <h2 style={styles.columnTitle}>🟢 Ready</h2>
                <div style={styles.columnSubtitle}>Orders ready for pickup</div>
                <div style={styles.waitTime}>Pick up now</div>
              </div>
              <div style={styles.ordersList}>
                {!Array.isArray(orders) || orders.length === 0 ? (
                  <div style={styles.emptyState}>No orders yet</div>
                ) : orders
                  .filter(order => order.itemsWithModifiers?.some(item => item.fulfillmentStatus === "READY"))
                  .map(order => (
                    <div key={order.guid} style={styles.orderCard}>
                      {isPhone ? (
                        // Phone layout: only order number, time, name
                        <div style={styles.phoneOrderCard}>
                          <div style={styles.phoneOrderInfo}>
                            <div style={styles.phoneOrderNumber}>#{order.orderNumber}</div>
                            <div style={styles.phoneOrderTime}>{new Date(order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          <div style={styles.phoneOrderName}>{order.tabName}</div>
                        </div>
                      ) : (
                        // Normal layout
                        <>
                          <div style={styles.orderCardHeader}>
                            <div style={styles.orderNumber}>#{order.orderNumber}</div>
                            <div style={styles.orderTime}>{new Date(order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          <div style={styles.orderTabName}>{order.tabName}</div>
                          <div style={styles.orderItemsList}>
                            {order.itemsWithModifiers?.map((item, idx) => (
                              <div key={idx} style={styles.orderItemRow}>
                                <span style={styles.itemQty}>{item.qty}x</span>
                                <span style={styles.itemName}>{item.name}</span>
                                {item.modifiers.length > 0 && (
                                  <div style={styles.itemMods}>{item.modifiers.join(", ")}</div>
                                )}
                              </div>
                            ))}
                          </div>
                          <div style={styles.orderFooter}>
                            <span style={styles.orderTotalAmount}>{order.total}</span>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
              </div>
            </div>
            
            <div style={styles.ordersColumn}>
              <div style={styles.columnHeader}>
                <h2 style={styles.columnTitle}>🟡 Cooking</h2>
                <div style={styles.columnSubtitle}>Orders being prepared</div>
                <div style={styles.waitTime}>Est. Wait: {calculateWaitTime()} min</div>
              </div>
              <div style={styles.ordersList}>
                {!Array.isArray(orders) || orders.length === 0 ? (
                  <div style={styles.emptyState}>No orders yet</div>
                ) : orders
                  .filter(order => !order.itemsWithModifiers?.some(item => item.fulfillmentStatus === "READY"))
                  .map(order => (
                    <div key={order.guid} style={styles.orderCard}>
                      {isPhone ? (
                        // Phone layout: only order number, time, name
                        <div style={styles.phoneOrderCard}>
                          <div style={styles.phoneOrderInfo}>
                            <div style={styles.phoneOrderNumber}>#{order.orderNumber}</div>
                            <div style={styles.phoneOrderTime}>{new Date(order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          <div style={styles.phoneOrderName}>{order.tabName}</div>
                        </div>
                      ) : (
                        // Normal layout
                        <>
                          <div style={styles.orderCardHeader}>
                            <div style={styles.orderNumber}>#{order.orderNumber}</div>
                            <div style={styles.orderTime}>{new Date(order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          <div style={styles.orderTabName}>{order.tabName}</div>
                          <div style={styles.orderItemsList}>
                            {order.itemsWithModifiers?.map((item, idx) => (
                              <div key={idx} style={styles.orderItemRow}>
                                <span style={styles.itemQty}>{item.qty}x</span>
                                <span style={styles.itemName}>{item.name}</span>
                                {item.modifiers.length > 0 && (
                                  <div style={styles.itemMods}>{item.modifiers.join(", ")}</div>
                                )}
                              </div>
                            ))}
                          </div>
                          <div style={styles.orderFooter}>
                            <span style={styles.orderTotalAmount}>{order.total}</span>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      ) : showGrill ? (
        <div style={styles.kdsContainer}>
          <div style={styles.kdsOrdersContainer}>
            {!Array.isArray(orders) || orders.length === 0 ? (
              <div style={styles.emptyState}>No orders yet</div>
            ) : (() => {
              const filteredOrders = orders
                .filter(order => !order.itemsWithModifiers?.some(item => item.fulfillmentStatus === "READY"))
                .sort((a, b) => new Date(a.openedDate) - new Date(b.openedDate));
              
              // Layout constants
              const availableHeight = window.innerHeight - 80;
              const cardPadding = 40;
              const cardHeaderHeight = 50;
              const continuedLabelHeight = 30;
              const itemRowHeight = 80;
              const cardGap = 16;
              const maxItemsPerCard = 5;
              
              // Phase 1: Build all cards, splitting large orders
              const allCards = [];
              filteredOrders.forEach(order => {
                const items = order.itemsWithModifiers || [];
                if (items.length > maxItemsPerCard) {
                  const totalParts = Math.ceil(items.length / maxItemsPerCard);
                  for (let i = 0; i < items.length; i += maxItemsPerCard) {
                    const chunk = items.slice(i, i + maxItemsPerCard);
                    const partNumber = Math.floor(i / maxItemsPerCard) + 1;
                    const isContinued = i > 0;
                    const h = cardPadding + cardHeaderHeight + (isContinued ? continuedLabelHeight : 0) + (chunk.length * itemRowHeight);
                    allCards.push({ order, items: chunk, isContinued, partNumber, totalParts, height: h });
                  }
                } else {
                  const h = cardPadding + cardHeaderHeight + (items.length * itemRowHeight);
                  allCards.push({ order, items, isContinued: false, height: h });
                }
              });
              
              // Phase 2: Pack cards into columns that fit within screen height
              const columns = [];
              let currentColumn = [];
              let currentHeight = 0;
              
              allCards.forEach(card => {
                const heightIfAdded = currentHeight + (currentColumn.length > 0 ? cardGap : 0) + card.height;
                if (heightIfAdded > availableHeight && currentColumn.length > 0) {
                  columns.push(currentColumn);
                  currentColumn = [card];
                  currentHeight = card.height;
                } else {
                  currentColumn.push(card);
                  currentHeight = heightIfAdded;
                }
              });
              if (currentColumn.length > 0) columns.push(currentColumn);
              
              return columns.map((column, colIdx) => (
                <div key={colIdx} style={styles.kdsColumn}>
                  {column.map((card, cardIdx) => (
                <div key={`${card.order.guid}-${cardIdx}`} style={styles.kdsOrderCard}>
                  <div style={styles.kdsOrderHeader}>
                    <div style={styles.kdsOrderNumber}>#{card.order.orderNumber}</div>
                    <div style={styles.kdsOrderTabName}>{card.order.tabName}</div>
                    <div style={styles.kdsOrderTime}>{new Date(card.order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  {card.isContinued && (
                    <div style={styles.kdsContinuedLabel}>... Continued ({card.partNumber}/{card.totalParts})</div>
                  )}
                  <div style={styles.kdsOrderItemsList}>
                    {card.items.map((item, idx) => {
                      const isCoffee = item.name.toLowerCase().includes('drip') || item.name.toLowerCase().includes('latte') || item.name.toLowerCase().includes('chai') || item.name.toLowerCase().includes('cubano') || item.name.toLowerCase().includes('cortado') || item.name.toLowerCase().includes('americano');
                      const isBagelOrBreakfast = item.name.toLowerCase().includes('bagel') || item.name.toLowerCase().includes('breakfast') || item.name.toLowerCase().includes('lox') || item.name.toLowerCase().includes('philly cheesesteak')|| item.name.toLowerCase().includes('chicken')|| item.name.toLowerCase().includes('pastrami');
                      return (
                        <div key={idx} style={styles.kdsOrderItemRow}>
                          <span style={{
                            ...styles.kdsItemName, 
                            fontSize:30,
                            backgroundColor: isCoffee ? '#8B4513' : 'transparent',
                            color: item.qty > 1 ? '#ff0000' : (isCoffee ? '#ffffff' : styles.kdsItemName.color),
                            padding: isCoffee ? '4px 8px' : '0',
                            borderRadius: isCoffee ? '4px' : '0'
                          }}>{item.qty}x {item.name}</span>
                          {item.modifiers.length > 0 && (
                            <div style={styles.kdsItemModsContainer}>
                              {(() => {
                                const proteins = ['Bacon', 'Sausage', 'Turkey Sausage', 'Chicken', 'Pastrami', 'Over Med', 'Scrambled', 'Over Easy', 'Over Hard'];
                                const modifierIndex = item.modifiers.findIndex(mod => proteins.includes(mod));
                                return item.modifiers[modifierIndex] ? (
                                  <div 
                                    style={{
                                      ...styles.kdsItemMods,
                                      fontSize: (isBagelOrBreakfast) ? 40 : styles.kdsItemMods.fontSize,
                                      color: (isBagelOrBreakfast) ? '#2c7be5' : styles.kdsItemMods.color,
                                      backgroundColor: (isBagelOrBreakfast) ? '#f0f0f0' : 'transparent',
                                      padding: (isBagelOrBreakfast) ? '4px 8px' : styles.kdsItemMods.paddingLeft,
                                      borderRadius: (isBagelOrBreakfast) ? '4px' : '0'
                                    }}
                                  >
                                    {!isCoffee && item.modifiers[modifierIndex]}
                                  </div>
                                ) : null;
                              })()}
                              {!isCoffee && item.modifiers.length > 1 && (
                                <div style={styles.kdsItemMods}>
                                  {item.modifiers.slice(1).join(', ')}
                                </div>
                              )}
                              {isCoffee && item.modifiers.length > 0 && (
                                <div style={styles.kdsItemMods}>
                                  {item.modifiers.join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      ) : showKDS ? (
        <div style={styles.kdsContainer}>
          {/* <h1 style={styles.kdsHeader}>🍳 Kitchen Display System</h1> */}
          <div style={styles.kdsOrdersContainer}>
            {!Array.isArray(orders) || orders.length === 0 ? (
              <div style={styles.emptyState}>No orders yet</div>
            ) : (() => {
              const filteredOrders = orders
                .filter(order => !order.itemsWithModifiers?.some(item => item.fulfillmentStatus === "READY"))
                .sort((a, b) => new Date(a.openedDate) - new Date(b.openedDate));
              
              // Layout constants
              const availableHeight = window.innerHeight - 80; // minus nav
              const cardPadding = 40;    // top/bottom padding + border
              const cardHeaderHeight = 50; // header row
              const continuedLabelHeight = 30;
              const itemRowHeight = 80;  // item name (30px font) + modifiers (40px bagel) + gaps
              const cardGap = 16;
              const maxItemsPerCard = 5; // hard cap — keeps every card readable
              
              // Phase 1: Build all cards, splitting large orders
              const allCards = [];
              filteredOrders.forEach(order => {
                const items = order.itemsWithModifiers || [];
                if (items.length > maxItemsPerCard) {
                  const totalParts = Math.ceil(items.length / maxItemsPerCard);
                  for (let i = 0; i < items.length; i += maxItemsPerCard) {
                    const chunk = items.slice(i, i + maxItemsPerCard);
                    const partNumber = Math.floor(i / maxItemsPerCard) + 1;
                    const isContinued = i > 0;
                    const h = cardPadding + cardHeaderHeight + (isContinued ? continuedLabelHeight : 0) + (chunk.length * itemRowHeight);
                    allCards.push({ order, items: chunk, isContinued, partNumber, totalParts, height: h });
                  }
                } else {
                  const h = cardPadding + cardHeaderHeight + (items.length * itemRowHeight);
                  allCards.push({ order, items, isContinued: false, height: h });
                }
              });
              
              // Phase 2: Pack cards into columns that fit within screen height
              const columns = [];
              let currentColumn = [];
              let currentHeight = 0;
              
              allCards.forEach(card => {
                const heightIfAdded = currentHeight + (currentColumn.length > 0 ? cardGap : 0) + card.height;
                if (heightIfAdded > availableHeight && currentColumn.length > 0) {
                  columns.push(currentColumn);
                  currentColumn = [card];
                  currentHeight = card.height;
                } else {
                  currentColumn.push(card);
                  currentHeight = heightIfAdded;
                }
              });
              if (currentColumn.length > 0) columns.push(currentColumn);
              
              return columns.map((column, colIdx) => (
                <div key={colIdx} style={styles.kdsColumn}>
                  {column.map((card, cardIdx) => (
                <div key={`${card.order.guid}-${cardIdx}`} style={styles.kdsOrderCard}>
                  <div style={styles.kdsOrderHeader}>
                    <div style={styles.kdsOrderNumber}>#{card.order.orderNumber}</div>
                    <div style={styles.kdsOrderTabName}>{card.order.tabName}</div>
                    <div style={styles.kdsOrderTime}>{new Date(card.order.openedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  {card.isContinued && (
                    <div style={styles.kdsContinuedLabel}>... Continued ({card.partNumber}/{card.totalParts})</div>
                  )}
                  <div style={styles.kdsOrderItemsList}>
                    {card.items.map((item, idx) => {
                      const isCoffee = item.name.toLowerCase().includes('drip') || item.name.toLowerCase().includes('latte') || item.name.toLowerCase().includes('chai') || item.name.toLowerCase().includes('cubano') || item.name.toLowerCase().includes('cortado') || item.name.toLowerCase().includes('americano');
                      const isBagelOrBreakfast = item.name.toLowerCase().includes('bagel') || item.name.toLowerCase().includes('breakfast') || item.name.toLowerCase().includes('lox') || item.name.toLowerCase().includes('philly cheesesteak')|| item.name.toLowerCase().includes('chicken')|| item.name.toLowerCase().includes('pastrami');
                      return (
                        <div key={idx} style={styles.kdsOrderItemRow}>
                          <span style={{
                            ...styles.kdsItemName, 
                            fontSize:30,
                            backgroundColor: isCoffee ? '#8B4513' : 'transparent',
                            color: item.qty > 1 ? '#ff0000' : (isCoffee ? '#ffffff' : styles.kdsItemName.color),
                            padding: isCoffee ? '4px 8px' : '0',
                            borderRadius: isCoffee ? '4px' : '0'
                          }}>{item.qty}x {item.name}</span>
                          {item.modifiers.length > 0 && (
                            <div style={styles.kdsItemModsContainer}>
                              {(() => {
                                const bagelFlavors = ['Everything', 'Jalapeño Cheddar', 'Plain', 'Rosemary Salt', 'Sesame', 'Egg Everything', 'Egg', 'Cinnamon Raisin', 'Blueberry', 'Onion', 'Garlic'];
                                const modifierIndex = item.modifiers.findIndex(mod => bagelFlavors.includes(mod));
                                return item.modifiers[modifierIndex] ? (
                                  <div 
                                    style={{
                                      ...styles.kdsItemMods,
                                      fontSize: (isBagelOrBreakfast) ? 40 : styles.kdsItemMods.fontSize,
                                      color: (isBagelOrBreakfast) ? '#2c7be5' : styles.kdsItemMods.color,
                                      backgroundColor: (isBagelOrBreakfast) ? '#f0f0f0' : 'transparent',
                                      padding: (isBagelOrBreakfast) ? '4px 8px' : styles.kdsItemMods.paddingLeft,
                                      borderRadius: (isBagelOrBreakfast) ? '4px' : '0'
                                    }}
                                  >
                                    {!isCoffee && item.modifiers[modifierIndex].replace('Jalapeño Cheddar', 'Jalapeño Chdr')}
                                  </div>
                                ) : null;
                              })()}
                              {!isCoffee && item.modifiers.length > 1 && (
                                <div style={styles.kdsItemMods}>
                                  {item.modifiers.slice(1).join(', ')}
                                </div>
                              )}
                              {isCoffee && item.modifiers.length > 0 && (
                                <div style={styles.kdsItemMods}>
                                  {item.modifiers.join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      ) : showPrepShopList ? (
        <div style={styles.prepShopContainer}>
          <div style={styles.prepShopGrid}>
            <div style={styles.prepShopColumn}>
              <h2 style={styles.prepShopTitle}>📋 Prep List</h2>
              <div style={styles.prepShopList}>
                {(() => {
                  const prepItems = items
                    .filter(item => item.prepList)
                    .filter(item => {
                      const countsArr = Array.isArray(item.counts) ? item.counts : [];
                      const entry = countsArr.find(c => c.location === "Revcon 1");
                      const count = (entry ? entry.value : 0) ?? 0;
                      const threshold = item.threshold || 2;
                      return count < threshold;
                    })
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  
                  return prepItems.length === 0 ? (
                    <div style={styles.emptyState}>Nothing to prep!</div>
                  ) : prepItems.map(item => {
                    const countsArr = Array.isArray(item.counts) ? item.counts : [];
                    const entry = countsArr.find(c => c.location === "Revcon 1");
                    const count = (entry ? entry.value : 0) ?? 0;
                    return (
                      <div key={item._id} style={styles.prepShopItem}>
                        <span style={styles.prepShopItemName}>{item.name}</span>
                        <span style={styles.prepShopItemCount}>Current: {count} / Threshold: {item.threshold || 2}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            <div style={styles.prepShopColumn}>
              <h2 style={styles.prepShopTitle}>🛒 Shop List</h2>
              <div style={styles.prepShopList}>
                {(() => {
                  const shopItems = items
                    .filter(item => item.shopList)
                    .filter(item => {
                      const countsArr = Array.isArray(item.counts) ? item.counts : [];
                      const entry = countsArr.find(c => c.location === "Revcon 1");
                      const count = (entry ? entry.value : 0) ?? 0;
                      const threshold = item.threshold || 2;
                      return count < threshold;
                    })
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  
                  return shopItems.length === 0 ? (
                    <div style={styles.emptyState}>All stocked up!</div>
                  ) : shopItems.map(item => {
                    const countsArr = Array.isArray(item.counts) ? item.counts : [];
                    const entry = countsArr.find(c => c.location === "Revcon 1");
                    const count = (entry ? entry.value : 0) ?? 0;
                    return (
                      <div key={item._id} style={styles.prepShopItem}>
                        <span style={styles.prepShopItemName}>{item.name}</span>
                        <span style={styles.prepShopItemCount}>Current: {count} / Threshold: {item.threshold || 2}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      ) : showStatistics ? (
        <div style={styles.statsContainer}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h1 style={styles.statsHeader}>📊 Daily Statistics</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 16, fontWeight: 'bold' }}>Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: 16,
                  borderRadius: 8,
                  border: '2px solid #333',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          
          {/* Bagel Orders Section */}
          {(() => {
            // Aggregate all items from orders
            const itemCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const itemName = item.name;
                const qty = parseInt(item.qty) || 1;
                if (itemCounts[itemName]) {
                  itemCounts[itemName] += qty;
                } else {
                  itemCounts[itemName] = qty;
                }
              });
            });
            
            // Filter for bagel items
            const bagelItems = ['Breakfast', 'Bagel', 'Lox', 'Chicken', 'Salmon Lox', 'Pastrami', 'Pastrami Sammich', 'Veggie', 'Philly Cheesesteak'];
            const bagelCounts = bagelItems.map(name => [name, itemCounts[name] || 0]);
            const bagelTotal = bagelCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Bagel Orders - Total: {bagelTotal}</h2>
                <div style={styles.statsGrid}>
                  {bagelCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Bagel Flavors Section */}
          {(() => {
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Filter for bagel flavor modifiers
            const bagelFlavors = ['Everything', 'Jalapeño Cheddar', 'Plain', 'Rosemary Salt', 'Sesame', 'Egg Everything', 'Egg', 'Cinnamon Raisin', 'Blueberry', 'Onion', 'Garlic'];
            const flavorCounts = bagelFlavors.map(name => [name, modifierCounts[name] || 0]);
            const flavorTotal = flavorCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Bagel Flavors - Total: {flavorTotal}</h2>
                <div style={styles.statsGrid}>
                  {flavorCounts.map(([flavorName, count]) => (
                    <div key={flavorName} style={styles.statCard}>
                      <div style={styles.statItemName}>{flavorName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Coffee Orders Section */}
          {(() => {
            // Aggregate all items from orders
            const itemCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const itemName = item.name;
                const qty = parseInt(item.qty) || 1;
                if (itemCounts[itemName]) {
                  itemCounts[itemName] += qty;
                } else {
                  itemCounts[itemName] = qty;
                }
              });
            });
            
            // Filter for coffee items
            const coffeeItems = ['Latte', 'Drip', 'Iced Latte', 'Cappuccino', 'Cortado', 'Cubano', 'Americano'];
            const coffeeCounts = coffeeItems.map(name => [name, itemCounts[name] || 0]);
            const coffeeTotal = coffeeCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Coffee Orders - Total: {coffeeTotal}</h2>
                <div style={styles.statsGrid}>
                  {coffeeCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Milks Section */}
          {(() => {
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Filter for milk modifiers
            const milkItems = ['Whole', 'Oat', 'Almond'];
            const milkCounts = milkItems.map(name => [name, modifierCounts[name] || 0]);
            const milkTotal = milkCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Milks - Total: {milkTotal}</h2>
                <div style={styles.statsGrid}>
                  {milkCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Other Drinks Section */}
          {(() => {
            // Aggregate all items from orders
            const itemCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const itemName = item.name;
                const qty = parseInt(item.qty) || 1;
                if (itemCounts[itemName]) {
                  itemCounts[itemName] += qty;
                } else {
                  itemCounts[itemName] = qty;
                }
              });
            });
            
            // Filter for other drink items
            const otherDrinkItems = ['12oz Orange Juice', 'Mexican Coke 500ml', '12 oz Orange Juice', "Richard's sparkling Rainwater", "Richards Still Water", "Richards Still Rainwater", 'Chai', 'Milk', 'Hot Chocolate'];
            const otherDrinkCounts = otherDrinkItems.map(name => [name, itemCounts[name] || 0]);
            const otherDrinkTotal = otherDrinkCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Other Drinks - Total: {otherDrinkTotal}</h2>
                <div style={styles.statsGrid}>
                  {otherDrinkCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Proteins Section */}
          {(() => {
            // Aggregate all items from orders
            const itemCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const itemName = item.name;
                const qty = parseInt(item.qty) || 1;
                if (itemCounts[itemName]) {
                  itemCounts[itemName] += qty;
                } else {
                  itemCounts[itemName] = qty;
                }
              });
            });
            
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Combine protein counts from different sources
            const proteinItems = [
              { name: 'Bacon', source: 'modifier' },
              { name: 'Add Bacon', source: 'modifier' },
              { name: 'Sausage', source: 'modifier' },
              { name: 'Add Sausage', source: 'modifier' },
              { name: 'Turkey Sausage', source: 'modifier' },
              { name: 'Add Turkey Sausage', source: 'modifier' },
              { name: 'Pastrami', source: 'modifier' },
              { name: 'Pastrami Sammich', source: 'item' },
              { name: 'Pastrami', source: 'item' },
              { name: 'Chicken', source: 'item' },
              { name: 'Salmon Lox', source: 'item' },
              { name: 'Add Salmon Lox', source: 'modifier' },
              { name: 'Smokey Ham', source: 'modifier' },
              
            ];
            
            const proteinCounts = proteinItems.map(({ name, source }) => {
              const count = source === 'item' ? (itemCounts[name] || 0) : (modifierCounts[name] || 0);
              return [name, count];
            });
            
            const proteinTotal = proteinCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Proteins - Total: {proteinTotal}</h2>
                <div style={styles.statsGrid}>
                  {proteinCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Eggs Section */}
          {(() => {
            // Aggregate all items from orders
            const itemCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const itemName = item.name;
                const qty = parseInt(item.qty) || 1;
                if (itemCounts[itemName]) {
                  itemCounts[itemName] += qty;
                } else {
                  itemCounts[itemName] = qty;
                }
              });
            });
            
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Combine egg counts from different sources
            const eggItems = [
              { name: 'Over Med', source: 'modifier' },
              { name: 'Scrambled', source: 'modifier' },
              { name: 'Over Hard', source: 'modifier' },
              { name: 'Over Easy', source: 'modifier' },
              { name: 'Add Egg - Over Easy', source: 'modifier' },
              { name: 'Add Egg - Over Medium', source: 'modifier' },
              { name: 'Add Egg - Over Hard', source: 'modifier' },
              { name: 'Add Egg - Scrambled', source: 'modifier' }
            ];
            
            const eggCounts = eggItems.map(({ name, source }) => {
              const count = source === 'item' ? (itemCounts[name] || 0) : (modifierCounts[name] || 0);
              return [name, count];
            });
            
            const eggTotal = eggCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Eggs - Total: {eggTotal}</h2>
                <div style={styles.statsGrid}>
                  {eggCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Cheeses Section */}
          {(() => {
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Filter for cheese modifiers
            const cheeseItems = ['Cheddar', 'Add Cheddar', 'American', 'Gruyere', 'Pepperjack'];
            const cheeseCounts = cheeseItems.map(name => [name, modifierCounts[name] || 0]);
            const cheeseTotal = cheeseCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Cheeses - Total: {cheeseTotal}</h2>
                <div style={styles.statsGrid}>
                  {cheeseCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Cream Cheeses Section */}
          {(() => {
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Filter for cream cheese modifiers
            const creamCheeseItems = [
              'Plain Schmear', 
              'Plain Cream Cheese', 
              'Bacon Jalapeño Cream Cheese', 
              'Bacon Jalapeño Schmear', 
              'Scallion Cream Cheese', 
              'Scallion Schmear', 
              'Strawberries And Cream Schmear', 
              'Strawberry and Cream Cheese', 
              'Walnut Honey Schmear', 
              'Walnut Honey Cream Cheese', 
              'Herbed Schmear', 
              'Herb Cream Cheese', 
              'Kalamata Olive Schmear', 
              'Kalamata Olive Cream Cheese', 
              'Sundried Tomato Schmear', 
              'Sundried Tomato Cream Cheese', 
              'Salmon Dill Schmear', 
              'Salmon Dill Cream Cheese',
              '**Cinnamon Toast Crunch**'
            ];
            const creamCheeseCounts = creamCheeseItems.map(name => [name, modifierCounts[name] || 0]);
            const creamCheeseTotal = creamCheeseCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Cream Cheeses - Total: {creamCheeseTotal}</h2>
                <div style={styles.statsGrid}>
                  {creamCheeseCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Veggies Section */}
          {(() => {
            // Aggregate all modifiers from orders
            const modifierCounts = {};
            orders.forEach(order => {
              order.itemsWithModifiers?.forEach(item => {
                const qty = parseInt(item.qty) || 1;
                item.modifiers?.forEach(modifier => {
                  if (modifierCounts[modifier]) {
                    modifierCounts[modifier] += qty;
                  } else {
                    modifierCounts[modifier] = qty;
                  }
                });
              });
            });
            
            // Filter for veggie modifiers
            const veggieItems = ['Avocado', 'Tomato', 'Grilled Onion', 'Mixed Greens', 'Add Grilled Red Peppers', 'Add Scallions'];
            const veggieCounts = veggieItems.map(name => [name, modifierCounts[name] || 0]);
            const veggieTotal = veggieCounts.reduce((sum, [_, count]) => sum + count, 0);
            
            return (
              <>
                <h2 style={styles.statsSectionTitle}>Veggies - Total: {veggieTotal}</h2>
                <div style={styles.statsGrid}>
                  {veggieCounts.map(([itemName, count]) => (
                    <div key={itemName} style={styles.statCard}>
                      <div style={styles.statItemName}>{itemName}</div>
                      <div style={styles.statCount}>{count}</div>
                      <div style={styles.statLabel}>sold today</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          
          {/* Items Section */}
          <h2 style={styles.statsSectionTitle}>Items</h2>
          <div style={styles.statsGrid}>
            {(() => {
              // Aggregate all items from orders
              const itemCounts = {};
              orders.forEach(order => {
                order.itemsWithModifiers?.forEach(item => {
                  const itemName = item.name;
                  const qty = parseInt(item.qty) || 1;
                  if (itemCounts[itemName]) {
                    itemCounts[itemName] += qty;
                  } else {
                    itemCounts[itemName] = qty;
                  }
                });
              });
              
              // Convert to array and sort by count descending
              const sortedItems = Object.entries(itemCounts)
                .sort((a, b) => b[1] - a[1]);
              
              return sortedItems.length === 0 ? (
                <div style={styles.emptyState}>No orders today</div>
              ) : sortedItems.map(([itemName, count]) => (
                <div key={itemName} style={styles.statCard}>
                  <div style={styles.statItemName}>{itemName}</div>
                  <div style={styles.statCount}>{count}</div>
                  <div style={styles.statLabel}>sold today</div>
                </div>
              ));
            })()}
          </div>
          
          {/* Modifiers Section */}
          <h2 style={styles.statsSectionTitle}>Modifiers</h2>
          <div style={styles.statsGrid}>
            {(() => {
              // Aggregate all modifiers from orders
              const modifierCounts = {};
              orders.forEach(order => {
                order.itemsWithModifiers?.forEach(item => {
                  const qty = parseInt(item.qty) || 1;
                  item.modifiers?.forEach(modifier => {
                    if (modifierCounts[modifier]) {
                      modifierCounts[modifier] += qty;
                    } else {
                      modifierCounts[modifier] = qty;
                    }
                  });
                });
              });
              
              // Convert to array and sort by count descending
              const sortedModifiers = Object.entries(modifierCounts)
                .sort((a, b) => b[1] - a[1]);
              
              return sortedModifiers.length === 0 ? (
                <div style={styles.emptyState}>No modifiers today</div>
              ) : sortedModifiers.map(([modifierName, count]) => (
                <div key={modifierName} style={styles.statCard}>
                  <div style={styles.statItemName}>{modifierName}</div>
                  <div style={styles.statCount}>{count}</div>
                  <div style={styles.statLabel}>used today</div>
                </div>
              ));
            })()}
          </div>
        </div>
      ) : showWeeklyStats ? (
        <div style={styles.statsContainer}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h1 style={styles.statsHeader}>📈 Weekly Bagel Flavors</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 16, fontWeight: 'bold', color: '#fff' }}>Week of:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: 16,
                  borderRadius: 8,
                  border: '2px solid #333',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>

          {(() => {
            const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const bagelFlavors = ['Everything', 'Jalapeño Cheddar', 'Plain', 'Rosemary Salt', 'Sesame', 'Egg Everything', 'Egg', 'Cinnamon Raisin', 'Blueberry', 'Onion', 'Garlic'];

            // Group orders by day of week (0=Sunday, 6=Saturday)
            const dayOrders = [[], [], [], [], [], [], []];
            weeklyOrders.forEach(order => {
              const date = new Date(order.openedDate);
              const day = date.getDay();
              dayOrders[day].push(order);
            });

            // Compute modifier counts per day
            const dayModifierCounts = dayOrders.map(dayOrds => {
              const counts = {};
              dayOrds.forEach(order => {
                order.itemsWithModifiers?.forEach(item => {
                  const qty = parseInt(item.qty) || 1;
                  item.modifiers?.forEach(modifier => {
                    counts[modifier] = (counts[modifier] || 0) + qty;
                  });
                });
              });
              return counts;
            });

            return (
              <div style={styles.weeklyChartContainer}>
                {/* Day header row */}
                <div style={styles.weeklyChartHeaderRow}>
                  <div style={styles.weeklyChartFlavorLabel}></div>
                  {dayLabels.map((label) => (
                    <div key={label} style={styles.weeklyChartDayLabel}>{label}</div>
                  ))}
                </div>

                {bagelFlavors.map(flavor => {
                  const counts = dayModifierCounts.map(mc => mc[flavor] || 0);
                  const total = counts.reduce((a, b) => a + b, 0);

                  return (
                    <div key={flavor} style={styles.weeklyChartRow}>
                      <div style={styles.weeklyChartFlavorLabel}>
                        {flavor} <span style={{ color: '#888', fontSize: 12 }}>({total})</span>
                      </div>
                      {counts.map((count, idx) => {
                        const countStyle = count > 30
                          ? { fontSize: 24, fontWeight: 800, color: '#d4af37' }
                          : count >= 10
                          ? { fontSize: 20, fontWeight: 800, color: '#2c7be5' }
                          : { fontSize: 16, fontWeight: 800, color: '#333' };
                        return (
                          <div key={idx} style={styles.weeklyChartCountCell}>
                            <div style={countStyle}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      ) : showRevcon1b ? (
        <div style={styles.revconContainer}>
          {Object.keys(fridgeCategories).map(cat => {
            const catItems = (fridgeCategories[cat] || []).map(name =>
              items.find(i => i.name === name)
            ).filter(Boolean);
            const filteredItems = catItems.filter(item => {
              if (!hideZero) return true;
              const countsArr = Array.isArray(item.counts) ? item.counts : [];
              const entry = countsArr.find(c => c.location === "Revcon 1");
              return ((entry ? entry.value : 0) ?? 0) !== 0;
            });
            const itemCount = filteredItems.length;
            return (
              <div key={cat} style={styles.revconCategory}>
                <button
                  type="button"
                  style={styles.revconCategoryHeader}
                  onClick={() => setRevconExpanded(prev => prev === cat ? null : cat)}
                >
                  <span style={styles.revconCategoryName}>{cat}</span>
                  <span style={styles.revconCategoryBadge}>{itemCount}</span>
                  <span style={{ ...styles.revconChevron, transform: revconExpanded === cat ? "rotate(180deg)" : "rotate(0deg)" }}>&#9662;</span>
                </button>
                {revconExpanded === cat && (
                  <div style={styles.revconItemsList}>
                    {filteredItems.length === 0 ? (
                      <div style={styles.revconEmpty}>No items</div>
                    ) : filteredItems.map(item => {
                      const countsArr = Array.isArray(item.counts) ? item.counts : [];
                      const entry = countsArr.find(c => c.location === "Revcon 1");
                      const count = (entry ? entry.value : 0) ?? 0;
                      const colorIndex = Math.min(count, colors.length - 1);
                      return (
                        <div
                          key={item._id}
                          style={{ ...styles.revconItem, background: colors[colorIndex] }}
                        >
                          {deleteConfirmId === item._id ? (
                            <div style={styles.revconEditRow}>
                              {editingItemId === item._id ? (
                                <>
                                  <div style={styles.revconEditName}>{item.name}</div>
                                  <div style={styles.revconEditControls}>
                                    <label style={styles.revconEditCheckbox}>
                                      <input type="checkbox" checked={editPrepList} onChange={e => setEditPrepList(e.target.checked)} />
                                      Prep
                                    </label>
                                    <label style={styles.revconEditCheckbox}>
                                      <input type="checkbox" checked={editShopList} onChange={e => setEditShopList(e.target.checked)} />
                                      Shop
                                    </label>
                                    <input
                                      type="number"
                                      value={editThreshold}
                                      onChange={e => setEditThreshold(parseInt(e.target.value) || 0)}
                                      style={styles.revconEditThreshold}
                                      placeholder="Thr"
                                      min="0"
                                      max="50"
                                    />
                                    <button style={styles.revconEditSave} onClick={() => { saveEditItem(); setDeleteConfirmId(null); }}>Save</button>
                                    <button style={styles.revconEditCancel} onClick={() => { cancelEditItem(); setDeleteConfirmId(null); }}>Back</button>
                                  </div>
                                </>
                              ) : (
                                <div style={styles.revconEditControls}>
                                  <button style={styles.revconDeleteBtn} onClick={() => handleDeleteItem(item._id)}>Delete</button>
                                  <button style={styles.revconEditBtn} onClick={() => startEditItem(item)}>Edit</button>
                                  <button style={styles.revconCancelBtn} onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                style={styles.revconDecrement}
                                onClick={() => adjustItem(item._id, -1)}
                                onMouseDown={() => startLongPress(item._id)}
                                onMouseUp={cancelLongPress}
                                onMouseLeave={cancelLongPress}
                                onTouchStart={() => startLongPress(item._id)}
                                onTouchEnd={cancelLongPress}
                              >
                                <span style={styles.revconDecrementIcon}>&#8722;</span>
                              </button>
                              <div style={styles.revconItemCenter}>
                                <span style={styles.revconItemName}>{item.name}</span>
                              </div>
                              <button
                                type="button"
                                style={styles.revconIncrement}
                                onClick={() => adjustItem(item._id, +1)}
                              >
                                <span style={styles.revconCount}>
                                  {isPercentageItem(item) ? `${Math.min(count * 25, 100)}%` : count}
                                </span>
                                <span style={styles.revconIncrementIcon}>+</span>
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {addingTo === cat ? (
                      <div style={styles.revconAddForm}>
                        <input
                          type="text"
                          placeholder="New item name..."
                          value={newItemName}
                          onChange={e => setNewItemName(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleAddItem(cat)}
                          style={styles.revconAddInput}
                          autoFocus
                        />
                        <div style={styles.revconAddOptions}>
                          <label style={styles.revconAddCheckbox}>
                            <input type="checkbox" checked={newItemIsPercent} onChange={e => setNewItemIsPercent(e.target.checked)} />
                            <span>%</span>
                          </label>
                          <label style={styles.revconAddCheckbox}>
                            <input type="checkbox" checked={newItemPrepList} onChange={e => setNewItemPrepList(e.target.checked)} />
                            <span>Prep</span>
                          </label>
                          <label style={styles.revconAddCheckbox}>
                            <input type="checkbox" checked={newItemShopList} onChange={e => setNewItemShopList(e.target.checked)} />
                            <span>Shop</span>
                          </label>
                          <input
                            type="number"
                            placeholder="Thr"
                            value={newItemThreshold}
                            onChange={e => setNewItemThreshold(parseInt(e.target.value) || 2)}
                            style={styles.revconAddThreshold}
                            min="0"
                            max="50"
                          />
                        </div>
                        <div style={styles.revconAddActions}>
                          <button style={styles.revconAddConfirm} onClick={() => handleAddItem(cat)}>Add Item</button>
                          <button style={styles.revconAddCancel} onClick={() => { 
                            setAddingTo(null); 
                            setNewItemName(""); 
                            setNewItemIsPercent(false); 
                            setNewItemPrepList(false);
                            setNewItemShopList(false);
                            setNewItemThreshold(2);
                          }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        style={styles.revconAddButton}
                        onClick={() => { 
                          setAddingTo(cat); 
                          setNewItemName(""); 
                          setNewItemIsPercent(false); 
                          setNewItemPrepList(false);
                          setNewItemShopList(false);
                          setNewItemThreshold(2);
                        }}
                      >+ Add Item</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : showPics ? (
        renderPicsGrid(picCategories)
      ) : showLittlePics ? (
        renderPicsGrid(littlePicCategories)
      ) : showStElmoPics ? (
        renderPicsGrid(stElmoPicCategories)
      ) : showCubesmartPics ? (
        renderPicsGrid(cubesmartPicCategories)
      ) : showPrepOnly ? (
        <div>
          <h2 style={styles.subheader}>Finished Prep Stuff</h2>
          <div style={styles.columnsRow}>
            {[0, 1].map(col => {
              const filtered = items
                .filter(item => item.category === "Finished Prep Stuff")
                .filter(item => {
                  if (!hideZero) return true;
                  const countsArr = Array.isArray(item.counts) ? item.counts : [];
                  const entry =
                    countsArr.find(c => c.location === activeLocation) ||
                    countsArr.find(c => c.location === "Overall Inventory");
                  return ((entry ? entry.value : item.count) ?? 0) !== 0;
                });
              const half = Math.ceil(filtered.length / 2);
              const colItems = col === 0 ? filtered.slice(0, half) : filtered.slice(half);
              return (
                <div key={col} style={styles.column}>
                  {colItems.map(item => {
                    const countsArr = Array.isArray(item.counts) ? item.counts : [];
                    const entry =
                      countsArr.find(c => c.location === activeLocation) ||
                      countsArr.find(c => c.location === "Overall Inventory");
                    const count = (entry ? entry.value : item.count) ?? 0;
                    const colorIndex = Math.min(count, colors.length - 1);
                    return (
                      <div
                        key={item._id}
                        style={{
                          ...styles.item,
                          background: colors[colorIndex]
                        }}
                      >
                        <button
                          type="button"
                          style={styles.nameButton}
                          onClick={() => adjustItem(item._id, -1)}
                        >
                          {item.name}
                        </button>
                        <button
                          type="button"
                          style={styles.countButton}
                          onClick={() => adjustItem(item._id, +1)}
                        >
                          {isPercentageItem(item) ? `${Math.min(count * 25, 100)}%` : count}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
      <div style={styles.columnsRow}>
        <div style={styles.column}>
          {["Produce", "Paper Goods"].map(category => (
            <div key={category}>
              <h2 style={styles.subheader}>{category}</h2>
              {items
                .filter(item => item.category === category)
                .filter(item => {
                  if (!hideZero) return true;
                  const countsArr = Array.isArray(item.counts) ? item.counts : [];
                  const entry =
                    countsArr.find(c => c.location === activeLocation) ||
                    countsArr.find(c => c.location === "Overall Inventory");
                  return ((entry ? entry.value : item.count) ?? 0) !== 0;
                })
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(item => {
                  const countsArr = Array.isArray(item.counts) ? item.counts : [];
                  const entry =
                    countsArr.find(c => c.location === activeLocation) ||
                    countsArr.find(c => c.location === "Overall Inventory");
                  const count = (entry ? entry.value : item.count) ?? 0;
                  const colorIndex = Math.min(count, colors.length - 1);
                  
                  if (editingItemId === item._id) {
                    return (
                      <div key={item._id} style={styles.editItemForm}>
                        <div style={styles.editItemName}>{item.name}</div>
                        <label style={styles.editItemCheckbox}>
                          <input type="checkbox" checked={editPrepList} onChange={e => setEditPrepList(e.target.checked)} />
                          Prep
                        </label>
                        <label style={styles.editItemCheckbox}>
                          <input type="checkbox" checked={editShopList} onChange={e => setEditShopList(e.target.checked)} />
                          Shop
                        </label>
                        <input
                          type="number"
                          value={editThreshold}
                          onChange={e => setEditThreshold(parseInt(e.target.value) || 2)}
                          style={styles.editItemThresholdInput}
                          min="0"
                          max="50"
                        />
                        <button style={styles.editItemSave} onClick={saveEditItem}>✓</button>
                        <button style={styles.editItemCancel} onClick={cancelEditItem}>✗</button>
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={item._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, item, category)}
                      style={{
                        ...styles.item,
                        background: colors[colorIndex],
                        cursor: "move",
                        opacity: draggedItem?._id === item._id ? 0.5 : 1
                      }}
                    >
                      <button
                        type="button"
                        style={styles.nameButton}
                        onClick={() => adjustItem(item._id, -1)}
                      >
                        {item.name}
                      </button>
                      <button
                        type="button"
                        style={styles.countButton}
                        onClick={() => adjustItem(item._id, +1)}
                      >
                        {isPercentageItem(item) ? `${Math.min(count * 25, 100)}%` : count}
                      </button>
                      <button
                        type="button"
                        style={styles.editButton}
                        onClick={() => startEditItem(item)}
                        title="Edit flags"
                      >
                        ⚙
                      </button>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
        <div style={styles.column}>
          {["Meat", "Coffee Stuff", "Dairy", "Miscellaneous"].map(category => (
            <div key={category}>
              <h2 style={styles.subheader}>{category}</h2>
              {items
                .filter(item => item.category === category)
                .filter(item => {
                  if (!hideZero) return true;
                  const countsArr = Array.isArray(item.counts) ? item.counts : [];
                  const entry =
                    countsArr.find(c => c.location === activeLocation) ||
                    countsArr.find(c => c.location === "Overall Inventory");
                  return ((entry ? entry.value : item.count) ?? 0) !== 0;
                })
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(item => {
                  const countsArr = Array.isArray(item.counts) ? item.counts : [];
                  const entry =
                    countsArr.find(c => c.location === activeLocation) ||
                    countsArr.find(c => c.location === "Overall Inventory");
                  const count = (entry ? entry.value : item.count) ?? 0;
                  const colorIndex = Math.min(count, colors.length - 1);
                  
                  if (editingItemId === item._id) {
                    return (
                      <div key={item._id} style={styles.editItemForm}>
                        <div style={styles.editItemName}>{item.name}</div>
                        <label style={styles.editItemCheckbox}>
                          <input type="checkbox" checked={editPrepList} onChange={e => setEditPrepList(e.target.checked)} />
                          Prep
                        </label>
                        <label style={styles.editItemCheckbox}>
                          <input type="checkbox" checked={editShopList} onChange={e => setEditShopList(e.target.checked)} />
                          Shop
                        </label>
                        <input
                          type="number"
                          value={editThreshold}
                          onChange={e => setEditThreshold(parseInt(e.target.value) || 2)}
                          style={styles.editItemThresholdInput}
                          min="0"
                          max="50"
                        />
                        <button style={styles.editItemSave} onClick={saveEditItem}>✓</button>
                        <button style={styles.editItemCancel} onClick={cancelEditItem}>✗</button>
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={item._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, item, category)}
                      style={{
                        ...styles.item,
                        background: colors[colorIndex],
                        cursor: "move",
                        opacity: draggedItem?._id === item._id ? 0.5 : 1
                      }}
                    >
                      <button
                        type="button"
                        style={styles.nameButton}
                        onClick={() => adjustItem(item._id, -1)}
                      >
                        {item.name}
                      </button>
                      <button
                        style={styles.countButton}
                        onClick={() => adjustItem(item._id, +1)}
                      >
                        {isPercentageItem(item) ? `${Math.min(count * 25, 100)}%` : count}
                      </button>
                      <button
                        type="button"
                        style={styles.editButton}
                        onClick={() => startEditItem(item)}
                        title="Edit flags"
                      >
                        ⚙
                      </button>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
};

const SERVER = SERVER_URL;

const colors = [
  "#d63333ff",   // 0
  "#f76707",  // 1
  "#f6c343",  // 2
  "#2c7be5",  // 3
  "#00d97e",  // 4
];

const styles = {
  container: {
    padding: 0,
    width: "100%",
    margin: 0,
    fontFamily: "system-ui"
  },
  header: {
    textAlign: "center"
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px",
    height: 52,
    background: "#1a1a2e",
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    marginBottom: 0
  },
  hamburgerButton: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 5,
    width: 44,
    height: 44,
    padding: "10px 8px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    borderRadius: 8,
    flexShrink: 0
  },
  hamburgerLine: {
    display: "block",
    height: 2,
    width: 24,
    background: "#fff",
    borderRadius: 2
  },
  topBarTitle: {
    fontSize: 17,
    fontWeight: "700",
    color: "#fff",
    flex: 1,
    textAlign: "center",
    letterSpacing: "0.3px"
  },
  onlineDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0
  },
  sidebarOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 200
  },
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    width: 280,
    height: "100%",
    background: "#1a1a2e",
    zIndex: 300,
    display: "flex",
    flexDirection: "column",
    boxShadow: "4px 0 20px rgba(0,0,0,0.35)",
    transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
    overflowY: "auto"
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  sidebarTitle: {
    fontSize: 22,
    fontWeight: "900",
    color: "#fff",
    letterSpacing: "0.5px"
  },
  sidebarClose: {
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontSize: 16,
    cursor: "pointer"
  },
  sidebarNav: {
    display: "flex",
    flexDirection: "column",
    padding: "12px 0",
    flex: 1
  },
  sidebarNavItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "14px 20px",
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: 16,
    fontWeight: "500",
    cursor: "pointer",
    textAlign: "left",
    letterSpacing: "0.2px",
    borderLeft: "3px solid transparent"
  },
  sidebarNavItemActive: {
    background: "rgba(44,123,229,0.25)",
    color: "#fff",
    fontWeight: "700",
    borderLeft: "3px solid #2c7be5"
  },
  sidebarDivider: {
    height: 1,
    background: "rgba(255,255,255,0.1)",
    margin: "8px 20px 8px 20px"
  },
  sidebarCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 20px",
    color: "rgba(255,255,255,0.8)",
    fontSize: 15,
    fontWeight: "500",
    cursor: "pointer"
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 14,
    cursor: "pointer"
  },
  navButton: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#f8f9fa",
    cursor: "pointer",
    fontSize: 14
  },
  navButtonPrimary: {
    background: "#2c7be5",
    color: "#fff",
    border: "none"
  },
  columnsRow: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start"
  },
  column: {
    flex: 1
  },
  subheader: {
    marginTop: 16,
    marginBottom: 8
  },
  item: {
    width: "100%",
    marginBottom: 8,
    fontSize: 16,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
    boxSizing: "border-box"
  },
  itemLeft: {
  flex: 1,
  textAlign: "left",
  cursor: "pointer"
},
nameButton: {
  flex: 1,
  textAlign: "left",
  cursor: "pointer",
  background: "transparent",
  color: "#fff",
  border: "none",
  padding: "10px 12px",
  fontSize: 16,
  boxSizing: "border-box"
},
countButton: {
  flex: 1,
  fontSize: 20,
  fontWeight: "bold",
  color: "#fff",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "10px 12px",
  boxSizing: "border-box"
},
  editButton: {
    fontSize: 16,
    color: "#fff",
    background: "rgba(255, 255, 255, 0.2)",
    border: "none",
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: 4,
    marginLeft: 4
  },
  editItemForm: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    padding: 10,
    background: "#f0f0f0",
    borderRadius: 8,
    marginBottom: 4,
    alignItems: "center"
  },
  editItemName: {
    fontWeight: "bold",
    fontSize: 14,
    width: "100%",
    marginBottom: 4
  },
  editItemCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer"
  },
  editItemThresholdInput: {
    width: 50,
    padding: "4px 6px",
    fontSize: 12,
    borderRadius: 4,
    border: "1px solid #ddd",
    textAlign: "center"
  },
  editItemSave: {
    padding: "4px 10px",
    fontSize: 14,
    fontWeight: "bold",
    background: "#00d97e",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer"
  },
  editItemCancel: {
    padding: "4px 10px",
    fontSize: 14,
    fontWeight: "bold",
    background: "#d63333",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer"
  },
  count: {
    fontSize: 24,
    fontWeight: "bold"
  },
  picsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 16
  },
  picCard: {
    border: "1px solid #ddd",
    borderRadius: 10,
    overflow: "hidden",
    background: "#f8f9fa"
  },
  picTitle: {
    textAlign: "center",
    margin: 0,
    padding: "10px 0",
    background: "#2c7be5",
    color: "#fff",
    fontSize: 16
  },
  picArea: {
    cursor: "pointer",
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  picImage: {
    width: "100%",
    height: "auto",
    display: "block"
  },
  picPlaceholder: {
    color: "#999",
    fontSize: 16,
    padding: 40
  },
  picTimestamp: {
    display: "block",
    fontSize: 11,
    fontWeight: "normal",
    opacity: 0.85,
    marginTop: 2
  },
  picCardDesktop: {
    border: "1px solid #ddd",
    borderRadius: 10,
    overflow: "hidden",
    background: "#f8f9fa",
    display: "flex",
    flexDirection: "column",
    minHeight: 0
  },
  picTitleDesktop: {
    textAlign: "center",
    margin: 0,
    padding: "6px 4px",
    background: "#1a1a2e",
    color: "#fff",
    fontSize: 13,
    flexShrink: 0
  },
  picAreaDesktop: {
    cursor: "pointer",
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden"
  },
  picImageDesktop: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    display: "block"
  },
  offlineBanner: {
    background: "#f76707",
    color: "#fff",
    textAlign: "center",
    padding: "8px 12px",
    borderRadius: 8,
    marginBottom: 8,
    fontWeight: "bold",
    fontSize: 14
  },
  addItemButton: {
    width: "100%",
    padding: "10px 0",
    fontSize: 20,
    fontWeight: "bold",
    background: "#e9ecef",
    border: "1px dashed #adb5bd",
    borderRadius: 10,
    cursor: "pointer",
    color: "#495057",
    marginTop: 4
  },
  addItemForm: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    marginTop: 4
  },
  addItemInput: {
    flex: 1,
    padding: "8px 10px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #ddd"
  },
  addItemCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 14,
    fontWeight: "bold",
    cursor: "pointer"
  },
  addItemThresholdInput: {
    width: 60,
    padding: "8px 6px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #ddd",
    textAlign: "center"
  },
  addItemConfirm: {
    padding: "8px 12px",
    fontSize: 14,
    fontWeight: "bold",
    background: "#00d97e",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  addItemCancel: {
    padding: "8px 10px",
    fontSize: 14,
    fontWeight: "bold",
    background: "#d63333",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  deleteConfirm: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    padding: "4px 8px"
  },
  deleteYes: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: "bold",
    background: "#d63333",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  deleteNo: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: "bold",
    background: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  ordersContainer: {
    padding: "0",
    height: "calc(100vh - 52px)",
    overflow: "hidden",
    background: "#f5f5f5"
  },
  ordersGrid: {
    display: "grid",
    height: "100%",
    gap: 0
  },
  ordersColumn: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden"
  },
  columnHeader: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: "20px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
  },
  columnTitle: {
    fontSize: 28,
    fontWeight: "800",
    margin: 0,
    marginBottom: 8,
    letterSpacing: "-0.5px"
  },
  columnSubtitle: {
    fontSize: 14,
    opacity: 0.9,
    fontWeight: "400"
  },
  waitTime: {
    fontSize: 16,
    fontWeight: "700",
    marginTop: 8,
    padding: "8px 16px",
    background: "rgba(255, 255, 255, 0.2)",
    borderRadius: 20,
    display: "inline-block",
    letterSpacing: "0.5px"
  },
  ordersList: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    background: "#f5f5f5"
  },
  emptyState: {
    textAlign: "center",
    color: "#999",
    fontSize: 16,
    padding: "60px 20px",
    fontStyle: "italic"
  },
  orderCard: {
    background: "#fff",
    borderRadius: 12,
    padding: "16px",
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e0e0e0",
    transition: "transform 0.2s, box-shadow 0.2s"
  },
  orderCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: "2px solid #f0f0f0"
  },
  orderNumber: {
    fontSize: 24,
    fontWeight: "800",
    color: "#667eea",
    letterSpacing: "-0.5px"
  },
  orderTime: {
    fontSize: 14,
    color: "#666",
    fontWeight: "600"
  },
  orderTabName: {
    fontSize: 18,
    fontWeight: "700",
    color: "#333",
    marginBottom: 12
  },
  orderItemsList: {
    marginBottom: 12
  },
  orderItemRow: {
    padding: "8px 0",
    borderBottom: "1px solid #f5f5f5"
  },
  itemQty: {
    display: "inline-block",
    minWidth: "30px",
    fontWeight: "700",
    color: "#667eea",
    fontSize: 14
  },
  itemName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#333",
    marginLeft: 8
  },
  itemMods: {
    fontSize: 12,
    color: "#888",
    fontStyle: "italic",
    marginTop: 4,
    marginLeft: 38
  },
  orderFooter: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 12,
    borderTop: "2px solid #f0f0f0"
  },
  orderTotalAmount: {
    fontSize: 20,
    fontWeight: "800",
    color: "#28a745"
  },
  phoneOrderCard: {
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  phoneOrderInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    alignItems: "center",
    textAlign: "center"
  },
  phoneOrderNumber: {
    fontSize: 32,
    fontWeight: "900",
    color: "#667eea",
    lineHeight: 1
  },
  phoneOrderTime: {
    fontSize: 12,
    color: "#888",
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  phoneOrderName: {
    fontSize: 14,
    fontWeight: "700",
    color: "#333",
    textAlign: "center",
    marginTop: 4,
    lineHeight: 1.3
  },
  prepShopContainer: {
    padding: 20,
    maxWidth: 1200,
    margin: "0 auto"
  },
  prepShopGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20
  },
  prepShopColumn: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  prepShopTitle: {
    fontSize: 24,
    fontWeight: "800",
    marginBottom: 20,
    color: "#333",
    borderBottom: "3px solid #667eea",
    paddingBottom: 12
  },
  prepShopList: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  prepShopItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#f8f9fa",
    borderRadius: 8,
    border: "1px solid #e0e0e0"
  },
  prepShopItemName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333"
  },
  prepShopItemCount: {
    fontSize: 14,
    color: "#666",
    fontWeight: "500"
  },
  statsContainer: {
    padding: 20,
    margin: "0 auto",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    minHeight: "calc(100vh - 52px)"
  },
  statsHeader: {
    fontSize: 36,
    fontWeight: "900",
    color: "#fff",
    textAlign: "center",
    marginBottom: 30,
    textShadow: "0 2px 4px rgba(0,0,0,0.2)"
  },
  statsSectionTitle: {
    fontSize: 24,
    fontWeight: "800",
    color: "#fff",
    marginTop: 40,
    marginBottom: 20,
    paddingLeft: 10,
    textShadow: "0 1px 3px rgba(0,0,0,0.2)"
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 16,
    padding: "0 10px"
  },
  statCard: {
    background: "#fff",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    minHeight: 140
  },
  statItemName: {
    fontSize: 16,
    fontWeight: "700",
    color: "#333",
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 1.3
  },
  statCount: {
    fontSize: 48,
    fontWeight: "900",
    color: "#667eea",
    lineHeight: 1,
    marginBottom: 8
  },
  statLabel: {
    fontSize: 12,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: "600"
  },
  kdsContainer: {
    padding: 0,
    width: "100%",
    margin: 0,
    fontFamily: "system-ui"
  },
  kdsHeader: {
    textAlign: "center",
    fontSize: 24,
    marginBottom: 20,
    color: "#333"
  },
  kdsOrdersContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 16,
    padding: "0 20px 20px 20px",
    height: "calc(100vh - 80px)",
    overflowX: "auto",
    overflowY: "hidden"
  },
  kdsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 320,
    flexShrink: 0
  },
  kdsOrderCard: {
    background: "#fff",
    border: "2px solid #e0e0e0",
    borderRadius: 12,
    padding: "12px 12px 8px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    width: 320
  },
  kdsOrderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: 6,
    gap: 8
  },
  kdsOrderNumber: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2c7be5"
  },
  kdsOrderTime: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333"
  },
  kdsOrderTabName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#333",
    flex: 1,
    textAlign: "center"
  },
  kdsOrderItemsList: {
    display: "flex",
    flexDirection: "column",
    gap: 6
  },
  kdsOrderItemRow: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "6px 0 4px 0",
    borderBottom: "1px solid #f5f5f5"
  },
  kdsItemQty: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#666"
  },
  kdsItemName: {
    fontSize: 15,
    color: "#333",
    fontWeight: "500"
  },
  kdsItemModsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    marginTop: 2
  },
  kdsItemMods: {
    fontSize: 20,
    color: "#555",
    fontWeight: "500",
    paddingLeft: 4
  },
  kdsContinuedLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#f76707",
    fontStyle: "italic",
    marginBottom: 8,
    paddingTop: 4
  },
  weeklyChartContainer: {
    background: "#fff",
    borderRadius: 16,
    padding: "20px 24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
  },
  weeklyChartHeaderRow: {
    display: "grid",
    gridTemplateColumns: "140px repeat(7, 1fr)",
    gap: 8,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: "2px solid #e0e0e0"
  },
  weeklyChartDayLabel: {
    fontSize: 14,
    fontWeight: "800",
    color: "#333",
    textAlign: "center"
  },
  weeklyChartRow: {
    display: "grid",
    gridTemplateColumns: "140px repeat(7, 1fr)",
    gap: 8,
    alignItems: "end",
    marginBottom: 16,
    minHeight: 80
  },
  weeklyChartFlavorLabel: {
    fontSize: 13,
    fontWeight: "700",
    color: "#333",
    display: "flex",
    alignItems: "center",
    paddingRight: 8
  },
  weeklyChartCountCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  weeklyChartCountValue: {
    fontSize: 16,
    fontWeight: "800",
    color: "#333"
  },
  // Revcon 1b Mobile-Optimized Styles
  revconContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 8px 80px 8px",
    maxWidth: 600,
    margin: "0 auto"
  },
  revconCategory: {
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8e8e8"
  },
  revconCategoryHeader: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "14px 16px",
    background: "#1a1a2e",
    border: "none",
    cursor: "pointer",
    gap: 10
  },
  revconCategoryName: {
    flex: 1,
    textAlign: "left",
    fontSize: 16,
    fontWeight: "700",
    color: "#fff",
    letterSpacing: "0.2px"
  },
  revconCategoryBadge: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: 13,
    fontWeight: "700",
    padding: "2px 10px",
    borderRadius: 12,
    minWidth: 24,
    textAlign: "center"
  },
  revconChevron: {
    fontSize: 16,
    color: "rgba(255,255,255,0.7)",
    transition: "transform 0.25s ease",
    marginLeft: 4
  },
  revconItemsList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "10px 10px 14px 10px"
  },
  revconEmpty: {
    color: "#999",
    textAlign: "center",
    padding: "20px 0",
    fontSize: 14,
    fontStyle: "italic"
  },
  revconItem: {
    display: "flex",
    alignItems: "stretch",
    borderRadius: 10,
    overflow: "hidden",
    minHeight: 52
  },
  revconDecrement: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 52,
    background: "rgba(0,0,0,0.15)",
    border: "none",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0
  },
  revconDecrementIcon: {
    fontSize: 24,
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1
  },
  revconItemCenter: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    minWidth: 0
  },
  revconItemName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  revconIncrement: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minWidth: 70,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    cursor: "pointer",
    padding: "8px 12px",
    flexShrink: 0
  },
  revconCount: {
    fontSize: 22,
    fontWeight: "800",
    color: "#fff",
    lineHeight: 1
  },
  revconIncrementIcon: {
    fontSize: 20,
    fontWeight: "700",
    color: "rgba(255,255,255,0.7)",
    lineHeight: 1
  },
  revconEditRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "12px 14px",
    width: "100%",
    boxSizing: "border-box"
  },
  revconEditName: {
    fontSize: 15,
    fontWeight: "700",
    color: "#fff"
  },
  revconEditControls: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center"
  },
  revconEditCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer"
  },
  revconEditThreshold: {
    width: 50,
    padding: "6px 8px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.3)",
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    textAlign: "center"
  },
  revconEditSave: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: "700",
    background: "#00d97e",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconEditCancel: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: "700",
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconDeleteBtn: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: "700",
    background: "#d63333",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconEditBtn: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: "700",
    background: "#2c7be5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconCancelBtn: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: "700",
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconAddForm: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "12px",
    background: "#f8f9fa",
    borderRadius: 10,
    border: "1px dashed #ccc"
  },
  revconAddInput: {
    width: "100%",
    padding: "12px 14px",
    fontSize: 16,
    borderRadius: 8,
    border: "1px solid #ddd",
    boxSizing: "border-box"
  },
  revconAddOptions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center"
  },
  revconAddCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 14,
    fontWeight: "600",
    cursor: "pointer",
    color: "#333"
  },
  revconAddThreshold: {
    width: 56,
    padding: "8px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #ddd",
    textAlign: "center"
  },
  revconAddActions: {
    display: "flex",
    gap: 10
  },
  revconAddConfirm: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: "700",
    background: "#00d97e",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconAddCancel: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: "700",
    background: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer"
  },
  revconAddButton: {
    width: "100%",
    padding: "14px 0",
    fontSize: 15,
    fontWeight: "600",
    background: "#f0f2f5",
    border: "1px dashed #bbb",
    borderRadius: 10,
    cursor: "pointer",
    color: "#555",
    letterSpacing: "0.3px"
  }
};

export default App;
